import React, { useMemo, useState } from "react"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import CollapsibleCardV2 from "../basics/interface/CollapsibleCardV2"
import FilteredSelectV2 from "../basics/input/FilteredSelectV2"
import { dateFrToISO, dateISOToFr } from "../../utilities/Addons.js"

const textarea = { ...gs.input, minHeight: 70, resize: "vertical" }
const flagRow = {
	display: "inline-flex",
	alignItems: "center",
	gap: 8,
	cursor: "pointer",
	fontSize: 13,
	color: COLORS.textMuted,
}

const Field = ({ label, children }) => (
	<div style={gs.fieldRow}>
		<label style={gs.label}>{label}</label>
		{children}
	</div>
)

const TABS = [
	{ key: "sinistre", label: "Sinistre", statusKey: "showSinistre" },
	{ key: "fautif", label: "Fautif", statusKey: "showFautif" },
	{ key: "expertise", label: "Expertise", statusKey: "showExpert" },
	{ key: "cle", label: "Clé supp.", statusKey: "showCleSupp" },
	{ key: "plaques", label: "CG + Plaques", statusKey: "showPlaques" },
	{ key: "autre", label: "Autre", statusKey: "showAutre" },
]

/**
 * Carte « Sinistres » : une seule carte repliable, six sous-onglets
 * (gs.sectionTabs). Une pastille sur l'onglet signale que des données y sont
 * présentes (remplace l'auto-ouverture des sous-blocs legacy) ; la carte
 * s'ouvre d'elle-même si au moins un volet est renseigné.
 *
 * GE-81 (Jordan 30.10) : les 4 dates en vrai champ date (comme l'atelier ;
 * l'état garde JJ-MM-AAAA, vide = null côté serveur) ; expert choisi dans la
 * base (`expert`, `expertNom` = « Nom Prénom ») OU tapé en texte libre
 * (`expert` remis à null) pendant la phase de test. Expert, n° de sinistre et
 * assurance synchronisés avec la fiche atelier liée côté serveur.
 */
const DepFicheSinistresV2 = ({
	dep,
	status,
	updateDep,
	updateDepField,
	experts,
	open,
	onToggle,
}) => {
	const firstWithData = TABS.find((t) => status[t.statusKey])
	const [tab, setTab] = useState(
		firstWithData ? firstWithData.key : "sinistre",
	)

	// Mêmes options que l'atelier (AtelierV2, expertOptions).
	const expertOptions = useMemo(
		() =>
			(experts || []).map((e) => ({
				id: e.id,
				label: `${e.nom} ${e.prenom}`,
				entite: e.entite_label,
			})),
		[experts],
	)

	const dateInput = (field) => (
		<input
			style={gs.input}
			type="date"
			value={dateFrToISO(dep[field])}
			onChange={(e) => updateDepField(field, dateISOToFr(e.target.value))}
		/>
	)

	const textInput = (field, value) => (
		<input
			style={gs.input}
			value={value}
			onChange={(e) => updateDep(field, e, "input")}
		/>
	)

	const panels = {
		sinistre: (
			<div style={gs.fieldGroup}>
				<Field label="Assurance">
					{textInput("assuranceSinistre", dep.assuranceSinistre)}
				</Field>
				<Field label="Num. sinistre">
					{textInput("numSinistre", dep.numSinistre)}
				</Field>
				<Field label="Franchise">
					{textInput("franchise", dep.franchise)}
				</Field>
				<Field label="Rabais franchise">
					{textInput("rabaisFranchise", dep.rabaisFranchise)}
				</Field>
				<Field label="Couverture location">
					{textInput("couvLocation", dep.couvLocation)}
				</Field>
			</div>
		),
		fautif: (
			<div style={gs.fieldGroup}>
				<Field label="Nom">
					{textInput("fautifNom", dep.fautifNom)}
				</Field>
				<Field label="Voiture">
					{textInput("fautifVoiture", dep.fautifVoiture)}
				</Field>
				<Field label="Plaques">
					{textInput("fautifPlaques", dep.fautifPlaques)}
				</Field>
				<Field label="Assurance">
					{textInput("fautifAssurance", dep.fautifAssurance)}
				</Field>
			</div>
		),
		expertise: (
			<div style={gs.fieldGroup}>
				<Field label="Date prévue">{dateInput("expertDate")}</Field>
				<Field label="Date passage">
					{dateInput("expertDateEffective")}
				</Field>
				<Field label="Expert">
					<FilteredSelectV2
						options={expertOptions}
						value={dep.expertNom || ""}
						defaultKey="label"
						optionKeys={["label", "entite"]}
						minChars={2}
						placeholder="Expert de la base ou texte libre…"
						onChange={(v) => {
							// Revue GE-81 (🟡3) : toujours, même si le formulaire
							// ne porte pas l'expert posé entre-temps par l'atelier
							// (le serveur le retire aussi, cf. DepannageView).
							updateDepField("expert", null)
							updateDepField("expertNom", v)
						}}
						onSelect={(o) => {
							updateDepField("expert", o ? o.id : null)
							updateDepField("expertNom", o ? o.label : "")
						}}
						adornment={
							dep.expert ? (
								<span
									title="Expert de la base"
									style={{
										width: 8,
										height: 8,
										borderRadius: 99,
										backgroundColor: COLORS.success,
										boxShadow: `0 0 0 3px ${COLORS.successLight}`,
									}}
								/>
							) : null
						}
					/>
				</Field>
			</div>
		),
		cle: (
			<div style={gs.fieldGroup}>
				<Field label="Clé supp.">
					{textInput("cleSupp", dep.cleSupp)}
				</Field>
				<Field label="Info clé supp.">
					<textarea
						style={textarea}
						rows={4}
						value={dep.infoCleSupp}
						onChange={(e) => updateDep("infoCleSupp", e, "input")}
					/>
				</Field>
			</div>
		),
		plaques: (
			<div style={gs.fieldGroup}>
				<Field label="Carte grise">
					<label style={flagRow}>
						<input
							type="checkbox"
							checked={dep.carteGrise}
							onChange={(e) =>
								updateDep("carteGrise", e, "checkbox")
							}
						/>
						{dep.carteGrise ? "Oui" : "Non"}
					</label>
				</Field>
				<Field label="Reprise le">{dateInput("carteGriseDate")}</Field>
				<Field label="Infos">
					<textarea
						style={textarea}
						rows={3}
						value={dep.infoCarteGrise}
						onChange={(e) =>
							updateDep("infoCarteGrise", e, "input")
						}
					/>
				</Field>
				<Field label="Plaques">
					<label style={flagRow}>
						<input
							type="checkbox"
							checked={dep.avecPlaques}
							onChange={(e) =>
								updateDep("avecPlaques", e, "checkbox")
							}
						/>
						{dep.avecPlaques ? "Oui" : "Non"}
					</label>
				</Field>
				<Field label="Reprises le">
					{dateInput("plaquesReprisLe")}
				</Field>
				<Field label="Infos">
					<textarea
						style={textarea}
						rows={3}
						value={dep.infoAvecPlaques}
						onChange={(e) =>
							updateDep("infoAvecPlaques", e, "input")
						}
					/>
				</Field>
			</div>
		),
		autre: (
			<div style={gs.fieldGroup}>
				<Field label="Effets personnels">
					{textInput("effetsPers", dep.effetsPers)}
				</Field>
				<Field label="Autre">
					<textarea
						style={textarea}
						rows={4}
						value={dep.polAutre}
						onChange={(e) => updateDep("polAutre", e, "input")}
					/>
				</Field>
			</div>
		),
	}

	return (
		<CollapsibleCardV2
			title="Sinistres"
			defaultOpen={status.sinistre}
			open={open}
			onToggle={onToggle}
		>
			<div style={{ ...gs.sectionTabs, flexWrap: "wrap" }}>
				{TABS.map((t) => (
					<button
						key={t.key}
						type="button"
						style={{
							...gs.sectionTab(tab === t.key),
							display: "inline-flex",
							alignItems: "center",
							gap: 7,
						}}
						onClick={() => setTab(t.key)}
					>
						{t.label}
						{status[t.statusKey] && (
							<span
								title="Données renseignées"
								style={{
									width: 7,
									height: 7,
									borderRadius: 99,
									backgroundColor:
										tab === t.key
											? COLORS.accent
											: COLORS.warning,
									display: "inline-block",
								}}
							/>
						)}
					</button>
				))}
			</div>
			<div style={{ maxWidth: 640 }}>{panels[tab]}</div>
		</CollapsibleCardV2>
	)
}

export default DepFicheSinistresV2
